import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { needsAccountTypeChoice, useAuthStore } from '../../state/authStore';
import { Button } from '../../components/ui/button';
import { supabase, supabaseEnabled } from '../../lib/supabase';
import { homeForUserType } from '../../components/layout/RequireUserType';

/** OAuth redirect target for Supabase sign-in providers (Google, etc.).
 *  supabase-js parses the URL fragment, persists the session, and fires
 *  SIGNED_IN via onAuthStateChange — which authStore.initialize is already
 *  subscribed to. We just wait for `isAuthenticated` to flip, then route the
 *  user to their role-appropriate home. */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const userType = useAuthStore((s) => s.user?.userType);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!supabaseEnabled) {
      setError('Auth provider is not configured for this deployment.');
      return;
    }
    let cancelled = false;
    // supabase-js handles the token exchange on import; poll getSession so we
    // aren't racing the store's own onAuthStateChange subscription.
    (async () => {
      for (let i = 0; i < 40 && !cancelled; i += 1) {
        const { data: { session }, error: err } = await supabase.auth.getSession();
        if (err) {
          setError(err.message);
          return;
        }
        if (session) return;
        await new Promise((r) => setTimeout(r, 150));
      }
      if (!cancelled) setError('Sign-in did not complete. Please try again.');
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      if (needsAccountTypeChoice(user)) {
        navigate('/choose-account-type', { replace: true });
        return;
      }
      navigate(homeForUserType(userType), { replace: true });
    }
  }, [isAuthenticated, user, userType, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4">
      <div className="text-center space-y-4">
        <div className="p-3 rounded-xl bg-emerald-600/20 inline-block">
          <Activity className="w-8 h-8 text-emerald-500 animate-pulse" />
        </div>
        {error ? (
          <>
            <p className="text-sm text-red-400 max-w-sm">{error}</p>
            <Button onClick={() => navigate('/login', { replace: true })} className="bg-emerald-600 hover:bg-emerald-700">
              Back to sign in
            </Button>
          </>
        ) : (
          <p className="text-sm text-gray-400">Signing you in\u2026</p>
        )}
      </div>
    </div>
  );
}
